import type { PortfolioBacktestFormStrategySetting } from './portfolioBacktestDuplicate';

/** The parts of a strategy's launch preflight that decide whether it can start. */
export interface PortfolioBacktestPreflightStrategyStatus {
    strategyId: number;
    candidates: readonly { id: number; isDefault: boolean }[];
    selectedOptionBuyingStrategyIds?: readonly number[];
    selectedExitCandidateIds: readonly number[];
    coverage: {
        requestStatus: string | null;
        failedBacktests: number;
        errorMessages: readonly string[];
    };
}

/** Coverage requests in these states never finish on their own. */
const UNRECOVERABLE_COVERAGE_REQUEST_STATUSES: ReadonlySet<string> = new Set(['failed', 'cancelled', 'stalled']);

/** Portfolios subscribe to dozens of strategies, so a longer list would bury the message beside Start. */
const MAX_LISTED_STRATEGY_NAMES = 3;

export const describeMissingSettings = (strategyNames: readonly string[]): string => {
    const listedNames = strategyNames.slice(0, MAX_LISTED_STRATEGY_NAMES).join(', ');
    const unlistedCount = strategyNames.length - MAX_LISTED_STRATEGY_NAMES;

    return unlistedCount > 0
        ? `Choose settings for ${strategyNames.length} strategies: ${listedNames}, and ${unlistedCount} more.`
        : `Choose settings for: ${listedNames}.`;
};

/**
 * Says why the preflight would refuse a strategy's selection, or returns null when it can start. The
 * Start button is disabled whenever this returns a reason, so the form shows it rather than block silently.
 */
export const describePreflightStrategyBlocker = (
    strategy: PortfolioBacktestPreflightStrategyStatus,
    { strategyName, isOptionsStrategy }: { strategyName: string; isOptionsStrategy: boolean }
): string | null => {
    // A rejected profile (e.g. the wrong direction) comes back with no candidates or selections, so its error
    // is the real reason and must win over the empty-selection messages it causes.
    if (strategy.coverage.errorMessages.length > 0) return `${strategyName}: ${strategy.coverage.errorMessages.join(' ')}`;

    if (isOptionsStrategy && (strategy.selectedOptionBuyingStrategyIds?.length ?? 0) === 0) {
        return `${strategyName}: choose at least one option buying strategy.`;
    }

    if (strategy.selectedExitCandidateIds.length === 0) return `${strategyName}: choose at least one exit candidate.`;

    if (strategy.coverage.failedBacktests > 0) return `${strategyName}: one or more required strategy backtests failed.`;

    if (strategy.coverage.requestStatus !== null && UNRECOVERABLE_COVERAGE_REQUEST_STATUSES.has(strategy.coverage.requestStatus)) {
        return `${strategyName}: its backtest coverage request is ${strategy.coverage.requestStatus}.`;
    }

    return null;
};

/**
 * Starts an empty exit selection from the strategy's default candidates. A strategy whose live deployment has
 * no exits would otherwise open with nothing selected while its default candidate is listed as the choice.
 * Returns `settings` itself when nothing changes, so a state update with it is a no-op.
 */
export const applyDefaultExitCandidates = (
    settings: Record<number, PortfolioBacktestFormStrategySetting>,
    preflightStrategies: readonly Pick<PortfolioBacktestPreflightStrategyStatus, 'strategyId' | 'candidates'>[]
): Record<number, PortfolioBacktestFormStrategySetting> => {
    let nextSettings = settings;

    for (const preflightStrategy of preflightStrategies) {
        const setting = settings[preflightStrategy.strategyId];

        if (!setting?.settingsId || setting.exitCandidateIds.length > 0) continue;

        const defaultExitCandidateIds = preflightStrategy.candidates
            .filter((candidate) => candidate.isDefault)
            .map((candidate) => candidate.id);

        if (defaultExitCandidateIds.length === 0) continue;

        if (nextSettings === settings) nextSettings = { ...settings };
        nextSettings[preflightStrategy.strategyId] = { ...setting, exitCandidateIds: defaultExitCandidateIds };
    }

    return nextSettings;
};
